import { PreferredContactMethodEnum } from '@inosio/payments-sdk';
import { Box, CircularProgress, Grid, IconButton, TextField, Typography } from '@mui/material';
import CopyToClipboard from 'react-copy-to-clipboard';
import copyclipboard from '../../../icons/copyclipboard.svg';
import { scrollStyle } from './WorkOrderDetails';
import { useEffect, useState } from 'react';

export interface WorkOrderDetailsReadOnlyFieldProps {
  /**
   * The width of the field in Material UI grid units.
   * Takes a value ranging from 0 to 12, 12 being full width, 6 being half etc
   */
  width: number;

  /**
   * The value to be displayed and copied
   */
  value: string;

  /**
   * Label to be displayed
   */
  label: string;

   /**
   *Optional prop provided when the field is displaying contact methods to show if its the
   *preffered contact method. Takes in the PreferredContactMethodEnum.
   */
  methodPreferred?: PreferredContactMethodEnum | null;

  /**
   * Callback that fires when the copy to clipboard icon is clicked in the field
   * @returns void
   */
  handleClickCopyToClipboard: () => void;

  /**
   * If true, allows the field to be edited
   */
  isEditable?: boolean;

  /**
   * If true, disables the field and shows a loading indicator
   */
  isUpdating?: boolean;

  /**
   * Callback to handle changes to the field value in edit mode
   */
  onValueChange?: (newValue: string) => void;

  /**
   * Callback that fires when the field loses focus and the value has changed.
   * @param newValue The updated value
   */
  onBlurUpdate?: (newValue: string) => void;
}

/**
 * A read-only field component with copy-to-clipboard capabilities.
 * @param props WorkOrderDetailsReadOnlyFieldProps
 * @returns
 */
export const WorkOrderDetailsReadOnlyField = (props: WorkOrderDetailsReadOnlyFieldProps) => {
  const [editableValue, setEditableValue] = useState<string>(props.value);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditableValue(event.target.value);
    if (props.onValueChange) {
      props.onValueChange(event.target.value);
    }
  };

  const handleBlur = () => {
    if (editableValue.trim() !== props.value.trim() && props.onBlurUpdate) {
      props.onBlurUpdate(editableValue.trim());
    }
  };

  useEffect(() => {
    setEditableValue(props.value)
  } , [
    props.value
  ])

  return (
    <Grid
      item
      xs={props.width}
      pl={2}
      pr={1}
      sx={{
        border: '2px solid #EEEEEE',
        borderRadius: '24px',
        height: 'auto',
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        marginTop: '2px',
        marginBottom: '5px',
        '&:hover': {
          border: props.isEditable ? '2px solid #000000' : '2px solid #EEEEEE'
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          backgroundColor: 'white',
          top: -7,
          left: 30,
          zIndex: 50,
          paddingLeft: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Work Sans',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '9px',
            color: '#14142B',
            marginRight: '10px',
          }}
        >
          {props.label}
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          backgroundColor: '#F1F0F5',
          top: -7,
          left: 30,
          zIndex: 55,
          paddingLeft: 1,
          borderRadius: '24px',
          marginLeft: props.methodPreferred === PreferredContactMethodEnum.Email ? '50px' : '100px',
          display: !props.methodPreferred ? 'none' : '',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Work Sans',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '9px',
            color: '#14142B',
            marginRight: '10px',
          }}
        >
          Preferred
        </Typography>
      </Box>
        <TextField
          value={editableValue}
          onChange={handleChange}
          onBlur={handleBlur}
          variant="standard"
          fullWidth
          disabled={!props.isEditable || props.isUpdating}
          InputProps={{
            disableUnderline: true,
            sx: {
              fontFamily: 'Work Sans',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '18px',
              color: '#14142B',
              paddingY: '5px',
              marginRight: '10px',
              maxHeight: '64px',
              overflowX: 'hidden',
              overflowY: 'auto',
              ...scrollStyle,
            },
          }}
        />
      
     {props.isUpdating ? <CircularProgress size={20} sx={{marginRight: '8px'}}/> :  <CopyToClipboard text={editableValue}>
        <IconButton onClick={props.handleClickCopyToClipboard}>
          <img src={copyclipboard} style={{ height: '20px', width: '20px' }} alt="copy" />
        </IconButton>
      </CopyToClipboard>}
    </Grid>
  );
};
